import { useState } from "react";
import { C, STATS, type StatId } from "@bd/content";
import { closeupPerson, look } from "@bd/art";
import { hireCost, levelCap, money, ageOf, perkChoice, retireAge, staffCap, staffPerkName, talentLabel, trainingCost, type Staff } from "@bd/sim";
import { Icon, Meter, Sheet, Tabs, Why } from "@bd/ui-kit";
import { me, useGame } from "../store";
import { dataUrl } from "../world/textures";
import { moraleLines } from "../lib/why";
import { t, tp } from "../i18n";
import { Clock } from "./Research";

const STAT_LABEL: Record<StatId, string> = { creativity: "Creativity", systems: "Systems", art: "Art", writing: "Writing", craft: "Craft", charisma: "Charisma" };
const stat = (k: StatId) => t(STAT_LABEL[k]);
const STAT_SHORT: Record<StatId, string> = { creativity: "CRE", systems: "SYS", art: "ART", writing: "WRI", craft: "CRA", charisma: "CHA" };
const short = (k: StatId) => t(STAT_SHORT[k], undefined, "stat");

function face(s: Staff) {
  return dataUrl(closeupPerson(look(s.look), { view: "front" }).crop(0, 0, 28, 28), 2);
}

function StatBars({ s }: { s: Staff }) {
  const main = C.role[s.role].main;
  return (
    <div className="stat-grid">
      {STATS.map((k) => (
        <div key={k} className={k === main ? "main" : undefined} title={`${stat(k)}: ${Math.floor(s.stats[k])}`}>
          <span className="spread"><span className="ellipsis caps" style={{ letterSpacing: "0.06em" }}>{short(k)}</span><span className="num">{Math.floor(s.stats[k])}</span></span>
          <Meter value={s.stats[k] / 100} color={k === main ? "var(--coral)" : "var(--ink)"} style={{ height: 3 }} label={stat(k)} />
        </div>
      ))}
    </div>
  );
}

/** Morale as one coloured number, with its breakdown behind (i). */
function moraleColor(m: number): string {
  return m > 60 ? "var(--green)" : m > 35 ? "var(--warn)" : "var(--bad)";
}

export function StaffScreen() {
  const game = useGame((s) => s.game)!;
  const set = useGame((s) => s.set);
  const cmd = useGame((s) => s.cmd);
  const toast = useGame((s) => s.toast);
  const co = me(game)!;
  const [tab, setTab] = useState<"team" | "hire">("team");
  const [show, setShow] = useState<"all" | "idle" | "unhappy" | "tired" | "strength">("all");
  const [sort, setSort] = useState<"level" | "morale" | "salary" | "role">("level");
  const filters: Record<typeof show, (s: Staff) => boolean> = {
    all: () => true,
    idle: (s) => !s.projectId && !s.training,
    unhappy: (s) => s.morale < 45,
    tired: (s) => s.burnout > 50,
    strength: (s) => !!perkChoice(s),
  };
  const sorters: Record<typeof sort, (a: Staff, b: Staff) => number> = {
    level: (a, b) => b.level - a.level,
    morale: (a, b) => a.morale - b.morale,
    salary: (a, b) => b.salary - a.salary,
    role: (a, b) => C.role[a.role].name.localeCompare(C.role[b.role].name) || b.level - a.level,
  };
  const count = (k: typeof show) => co.staff.filter(filters[k]).length;
  const team = co.staff.filter(filters[show]).sort(sorters[sort]);
  const hits = co.products.filter((p) => p.launched && p.score >= 8 && game.week - p.releaseWeek < 52).length;
  const run = async (c: Parameters<typeof cmd>[0], ok?: string) => {
    const r = await cmd(c);
    if (!r.ok) toast({ title: t("Not possible"), text: r.error ? t(r.error) : r.error, tone: "warn" });
    else if (ok) toast({ title: ok, tone: "good" });
  };
  const payroll = money(co.staff.reduce((a, s) => a + s.salary, 0));
  const title = (
    <span className="biz-title">
      {t("Staff")}
      <span className="biz-chip" title={tp(co.staff.length, "{n} person", "{n} people")}><Icon name="staff" size={14} /><span>{co.staff.length}<span className="muted" style={{ fontWeight: 400 }}>/{staffCap(co)}</span></span></span>
      <span className="biz-chip" title={t("payroll {pay}/wk", { pay: payroll })}><Icon name="coin" size={14} color="var(--gold)" />{t("{pay}/wk", { pay: payroll })}</span>
    </span>
  );
  return (
    <Sheet title={title} onClose={() => set({ screen: null })} drawer testId="staff">
      <Tabs tabs={[{ id: "team", label: t("Team") }, { id: "hire", label: `${t("Hire")} · ${co.candidates.length}` }]} value={tab} onChange={setTab} />
      {tab === "team" && (
        <div className="row" style={{ marginTop: 10, gap: 6, flexWrap: "wrap" }} data-testid="roster-tools">
          {([["all", "All", ""], ["idle", "Idle", ""], ["unhappy", "Unhappy", ""], ["tired", "Burnout", ""], ["strength", "★", "Strength to pick"]] as const).filter(([k]) => (k === "all" ? show !== "all" || (["idle", "unhappy", "tired", "strength"] as const).some((x) => count(x) > 0) : count(k) > 0 || show === k)).map(([k, label, tip]) => (
            <button key={k} className={`btn small ${show === k ? "primary" : "ghost"}`} title={tip ? t(tip) : undefined} onClick={() => setShow(k)}>{label === "★" ? label : t(label)}{k !== "all" ? ` ${count(k)}` : ""}</button>
          ))}
          <label className="biz-ic" style={{ gap: 4, marginLeft: "auto", fontSize: "0.85rem" }} title={t("Sort")}>
            <span aria-hidden className="muted">⇅</span>
            <select value={sort} onChange={(e) => setSort(e.target.value as typeof sort)} aria-label={t("Sort staff")}>
              <option value="level">{t("Level")}</option>
              <option value="morale">{t("Morale")}</option>
              <option value="salary">{t("Pay")}</option>
              <option value="role">{t("Role")}</option>
            </select>
          </label>
        </div>
      )}
      <div className="grid2" style={{ marginTop: 12, gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))" }}>
        {tab === "team" && team.length === 0 && <p className="muted">{t("Nobody here.")}</p>}
        {tab === "team" && team.map((s) => (
          <div key={s.id} className="card stack" data-testid="staff-card">
            <div className="row" style={{ alignItems: "flex-start", gap: 10 }}>
              <img src={face(s)} width={44} height={44} alt="" style={{ imageRendering: "pixelated", flex: "none" }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <span className="spread" style={{ alignItems: "baseline" }}>
                  <strong className="ellipsis">{s.founder ? t("You") : s.name}</strong>
                  <span className="row" style={{ gap: 4, flex: "none", fontSize: "0.82rem" }} title={t("Morale")}>
                    <Icon name="heart" size={14} color={moraleColor(s.morale)} />
                    <span className="num" style={{ color: moraleColor(s.morale) }}>{Math.round(s.morale)}</span>
                    <Why title={t("{name}: morale", { name: s.founder ? t("You") : s.name })} lines={moraleLines(co, s, hits, game.week).map((l) => ({ ...l, label: t(l.label) }))} />
                  </span>
                </span>
                <div className="muted ellipsis" style={{ fontSize: "0.8rem" }}>
                  {t(C.role[s.role].name)} · {t("Lv {n}", { n: s.level })} · {s.founder ? t("founder") : t("{pay}/wk", { pay: money(s.salary) })}
                  {!s.founder && retireAge(s) - ageOf(s, game.week) <= 2 ? ` · ${t("retires at {n}", { n: retireAge(s) })}` : ""}
                </div>
                <div className="tag-row">
                  {[...s.revealed.map((tr) => t(C.trait[tr].name)), ...(game.week - s.hiredWeek >= 8 && talentLabel(s) ? [t(talentLabel(s)!)] : [])].map((x) => <span key={x} className="tag">{x}</span>)}
                  {s.traits.length > s.revealed.length && <span className="tag muted" title={t("+1 hidden trait")}>?</span>}
                  {(s.perks ?? []).length > 0 && <span className="tag good" title={s.perks!.map((p) => t(staffPerkName(p))).join(" · ")}>★ {s.perks!.length}</span>}
                  {s.burnout > 50 && <span className="tag bad" title={t("Burnout")}>{t("Burning out")}</span>}
                </div>
              </div>
            </div>
            {perkChoice(s) && (
              <div className="inset stack" style={{ gap: 4, padding: 8 }} data-testid="perk-choice">
                <span className="biz-ic" style={{ fontSize: "0.82rem", color: "var(--green)" }} title={t("pick a strength for {name}, for good", { name: s.name.split(" ")[0] })}><strong>★ {t("Lv {n}", { n: perkChoice(s)!.level })}</strong></span>
                <div className="row" style={{ gap: 6, flexWrap: "wrap" }}>
                  {perkChoice(s)!.options.map((o) => (
                    <button key={o.id} className="btn small" style={{ whiteSpace: "normal", textAlign: "left", flex: "1 1 200px", minWidth: 0 }} title={t(o.desc)} onClick={() => run({ type: "staffPerk", staffId: s.id, perk: o.id }, `${s.name}: ${t(o.name)}`)}>{t(o.name)} <span className="muted" style={{ fontWeight: 400 }}>· {t(o.desc)}</span></button>
                  ))}
                </div>
              </div>
            )}
            <StatBars s={s} />
            <div className="row staff-actions">
              <select value={s.projectId ?? ""} onChange={(e) => run({ type: "assign", staffId: s.id, projectId: e.target.value || null })} aria-label={t("Assign to project")} style={{ flex: 1 }}>
                <option value="">{t("No project")}</option>
                {co.projects.map((p) => <option key={p.id} value={p.id}>{p.design.name}</option>)}
              </select>
              {s.training ? <span className="muted biz-ic" style={{ fontSize: "0.82rem", gap: 6 }} title={t("Training {stat}", { stat: stat(s.training.stat) })}>{stat(s.training.stat)} <span className="biz-ic num"><Clock />{s.training.weeksLeft}</span></span> : (
                <select value="" onChange={(e) => e.target.value && run({ type: "train", staffId: s.id, stat: e.target.value as StatId }, t("{name} starts training", { name: s.name }))} aria-label={t("Train a stat")}>
                  <option value="">{t("Train · {cost}", { cost: money(trainingCost(s, game.week)) })}</option>
                  {STATS.map((k) => <option key={k} value={k}>{stat(k)}</option>)}
                </select>
              )}
              {!s.founder && <button className="btn small ghost biz-ic" title={t("+10% pay")} aria-label={t("+10% pay")} onClick={() => run({ type: "raise", staffId: s.id, salary: Math.round(s.salary * 1.1) })}><Icon name="coin" size={14} color="var(--gold)" />+10%</button>}
              {!s.founder && <button className="btn small ghost" onClick={() => run({ type: "fire", staffId: s.id })}>{t("Let go")}</button>}
            </div>
          </div>
        ))}
        {tab === "hire" && co.candidates.map((c) => (
          <div key={c.id} className="card stack" data-testid="candidate">
            <div className="row" style={{ alignItems: "flex-start", gap: 10 }}>
              <img src={face(c)} width={44} height={44} alt="" style={{ imageRendering: "pixelated", flex: "none" }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <strong className="ellipsis" style={{ display: "block" }}>{c.name}</strong>
                <div className="muted ellipsis" style={{ fontSize: "0.8rem" }} title={t(C.role[c.role].does)}>{t(C.role[c.role].name)}</div>
                <div className="tag-row">
                  {c.revealed.map((tr) => <span key={tr} className="tag">{t(C.trait[tr].name)}</span>)}
                  {c.traits.length > c.revealed.length && <span className="tag muted" title={t("+1 hidden trait")}>?</span>}
                </div>
              </div>
            </div>
            <StatBars s={c} />
            <div className="spread">
              <span className="num biz-ic" title={t("Salary")}><Icon name="coin" size={14} color="var(--gold)" />{t("{pay}/wk", { pay: money(c.ask) })}</span>
              <button className="btn small primary" onClick={() => run({ type: "hire", candidateId: c.id })} disabled={co.staff.length >= staffCap(co)} title={t("+ {fee} to sign", { fee: money(hireCost(c)) })} data-testid="hire">{t("Hire · {fee}", { fee: money(hireCost(c)) })}</button>
            </div>
          </div>
        ))}
        {tab === "hire" && co.staff.length >= staffCap(co) && (
          <p className="muted biz-ic" style={{ gridColumn: "1 / -1", whiteSpace: "normal" }} data-testid="hire-limit">
            <Icon name="lock" size={14} />
            {co.staff.length >= levelCap(co)
              ? t("{place} full: move up in Finances.", { place: t(C.level[co.level].name) })
              : t("No free desk: build a room or add a desk.")}
          </p>
        )}
      </div>
    </Sheet>
  );
}
